import { Observable } from "rxjs";

import { LoginRequest } from "../auth/session.model";
import { Jurisdiction } from "../jurisdiction/jurisdiction.model";

export const enum KycState {
	Requested = "Requested",
	None = "None",
	Accepted = "Accepted",
	Processing = "Processing",
}

export const enum Gender {
	Male = "Male",
	Female = "Female",
	NotKnown = "NotKnown",
	NotApplicable = "NotApplicable",
}

export const enum CustomerDetailSection {
	CustomerId = "customerId",
	Profile = "profile",
	Address = "address",
	Subscriptions = "subscriptions",
	BasicInformation = "basicInformation",
	AllowedActions = "allowedActions",
	RequiredActions = "requiredActions",
	LastLogins = "lastLogins",
	LoginInformation = "loginInformation",
	Legal = "legal",
	Kyc = "kyc",
	AccountInformation = "accountInformation",
	Pgad = "pgad",
}

export const enum BankDetailsVerificationStatus {
	Unset = "Unset",
	NotChecked = "NotChecked",
	Pending = "Pending",
	Verified = "Verified",
	Denied = "Denied",
}

export const enum DisallowedActionCodes {
	M_CURRENTCUSTOMER_REASONUNSET = "M_CURRENTCUSTOMER_REASONUNSET",
	M_CURRENTCUSTOMER_DEPOSITLIMITUNSET = "M_CURRENTCUSTOMER_DEPOSITLIMITUNSET",
	M_CURRENTCUSTOMER_SESSIONLIMITUNSET = "M_CURRENTCUSTOMER_SESSIONLIMITUNSET",
	M_CURRENTCUSTOMER_ASSESSMENTREQUIRED = "M_CURRENTCUSTOMER_ASSESSMENTREQUIRED",
	M_CURRENTCUSTOMER_PRODUCTBLOCKED = "M_CURRENTCUSTOMER_PRODUCTBLOCKED",
	M_CURRENTCUSTOMER_SELFEXCLUDED = "M_CURRENTCUSTOMER_SELFEXCLUDED",
	M_CURRENTCUSTOMER_DOCUMENTVERIFICATIONPROCESSING = "M_CURRENTCUSTOMER_DOCUMENTVERIFICATIONPROCESSING",
}

export const enum AccountStatus {
	TemporaryAwaitingVerification = "TemporaryAwaitingVerification",
	Temporary = "Temporary",
	Permanent = "Permanent",
	TemporaryExpired = "TemporaryExpired",
	TemporaryLimited = "TemporaryLimited",
	TemporaryPreClosure = "TemporaryPreClosure",
}

export enum AllowedActions {
	Login = "login",
	Bet = "bet",
	BetCasino = "betCasino",
	BetSportsbook = "betSportsbook",
	Deposit = "deposit",
	Withdraw = "withdraw",
	ClaimBonus = "claimBonus",
	Cashout = "cashout",
}

export enum RequiredActions {
	AccountDataValidation = "ACCOUNT_DATA_VALIDATION",
	ProfileCompleteness = "PROFILE_COMPLETENESS",
	DocumentUpload = "DOCUMENT_UPLOAD",
}

export declare type CustomerCredentialType = "UsernamePasswordCredential";
export interface CredentialType {
	type: CustomerCredentialType;
}

export interface CustomerManagementState {
	customer?: Customer;
	emailAvailability?: EmailAvailability;
	hasAcceptedLatestTermsAndConditions?: boolean;
	hasDeclinedLatestTermsAndConditions?: boolean;
	hasDismissedLatestTermsAndConditions?: boolean;
	termsAndConditionsJurisdiction?: Jurisdiction;
	hasAcceptedLatestPrivacyPolicy?: boolean;
	hasAcceptedEntityLegalNotice?: boolean;
	hasUnsubscribedFromNewsletter?: boolean;
	isProfileUpdateSuccess?: boolean;
	isDepositTermsAndConditionsActive: boolean;
	credentials?: LoginRequest;
	expiredSessionUsername?: string;
	persistentData: Record<string, any>;
}

export interface EmailAvailability {
	email: string;
	isAvailable: boolean;
	OidcToken?: string;
}

export interface Customer {
	customerId: string;
	profile: Person;
	address: Address;
	basicInformation: BasicInformation;
	subscriptions: Subscriptions;
	loginInformation: UserLogin;
	allowedActions: AllowedAction;
	requiredActions: RequiredAction[];
	lastLogins: LoginInfo[];
	legal?: Legal;
	jurisdiction?: Jurisdiction;
	kyc: Kyc;
	credentialTypes: CredentialType[];
	accountInformation?: AccountInformation;
	hashedEmail: string;
	pgad?: Pgad;
	personalIdentificationDocument?: PersonalIdentificationDocument;
	playerState?: PlayerState;
	rawKycStatus?: Kyc;
}

export interface PlayerState {
	status: string;
}

export interface Address {
	street?: string;
	zipCode?: string;
	city?: string;
	country?: string;
	province?: string;
	provinceCode?: string;
	regionId?: number;
	fiscalRegionCode?: number;
	houseNumber?: string;
	state?: string;
	additive?: string;
	municipality?: string;
	department?: string;
	district?: string;
	residenceMunicipality?: string;
	departmentOfResidence?: string;
}

export interface Person {
	nationalIdNumber?: string;
	nativeFirstName?: string;
	nativeLastName?: string;
	firstName?: string;
	secondFirstName?: string;
	lastName?: string;
	infix?: string;
	secondSurname?: string;
	secondLastName?: string;
	maidenName?: string;
	occupation?: string;
	taxCode?: string;
	maritalStatus?: string;
	dniNumber?: string;
	personalCode?: number | string;
	personalCodeType?: string;
	nationality?: string;
	birthCountry?: string;
	birthCity?: string;
	gender?: Gender;
	dateOfBirth?: string;
	bankDetailsVerificationStatus?: BankDetailsVerificationStatus;
	idType?: string;
	idNumber?: string;
	shouldProfileValidationOccur?: boolean;
}

export interface ConfirmPegaCustomerProfileRequest {
	profileConfirmationToken: string;
	authenticationToken: string;
	nationality: string;
	personalCode: string;
	personalCodeType: string;
}

export interface ConfirmCogaCustomerProfileRequest {
	token: string;
	nationality: string;
	birthCountry: string;
}

export interface Subscriptions extends MarketingOffers {
	email?: string;
	phoneExtension?: string;
	phoneNumber?: string;
	isPhoneNumberVerified?: boolean;
	isEmailVerified?: boolean;
}

export interface MarketingOffers {
	acceptEmailOffers?: boolean;
	acceptSmsOffers?: boolean;
	acceptTelesalesOffers?: boolean;
}

export interface BasicInformation {
	pinCode?: string;
	currencyCode: string;
}

export interface UserLogin {
	username: string;
}

export interface AllowedActionOptions {
	isAllowed: boolean;
	disallowedReasons: DisallowedActionCodes[];
}

export interface RequiredActionOptions {
	actionTriggerId: string;
	action: string;
	isBlocking: boolean;
}

export interface Pgad {
	pinCode: string;
	accountCode: string;
}

export interface AllowedAction {
	[AllowedActions.Login]: AllowedActionOptions;
	[AllowedActions.Bet]: AllowedActionOptions;
	[AllowedActions.BetCasino]?: AllowedActionOptions;
	[AllowedActions.BetSportsbook]?: AllowedActionOptions;
	[AllowedActions.Deposit]: AllowedActionOptions;
	[AllowedActions.Withdraw]: AllowedActionOptions;
	[AllowedActions.ClaimBonus]: AllowedActionOptions;
	[AllowedActions.Cashout]?: AllowedActionOptions;
}

export interface RequiredAction {
	requiredActionId: string;
	actionType: RequiredActions;
	priority: number;
	activeFrom: string;
	triggers: RequiredActionOptions[];
}

export interface DeviceInfo {
	name: string;
	type: string;
	experience: string;
	isMobile: boolean;
}

export interface LoginInfo {
	timestamp: string;
	ipAddress: string;
	device: DeviceInfo;
}

export interface Legal {
	termsAndConditions?: boolean;
	/**
	 * Uk specific. Before the first deposit user needs to accept
	 * the deposit terms and conditions.
	 */
	depositTermsAndConditions?: boolean;
}

export interface AccountInformation {
	accountStatus: string;
	accountExpiry: string;
	accountCreationDate?: string;
}

export interface Kyc {
	state: KycState;
	lastChangeTimestamp: string | undefined;
	subStatus?: KycSubStatus;
}

export interface KycSubStatus {
	name: string;
	age: string;
	address: string;
	bank: string;
}

export interface PersonalIdentificationDocument {
	documentType?: string;
	documentTypeId?: number;
	documentNumber?: string;
	issueDate?: string;
	expiryDate?: string;
	documentIssuer?: string;
	issuePlace?: string;
}

export abstract class CustomerManagementService {
	abstract getCustomer(): Observable<Customer>;

	abstract getCustomerDetail(section?: CustomerDetailSection): Observable<Partial<Customer>>;

	abstract updateCustomer(customer: Partial<Customer>): Observable<Customer>;

	abstract verifyToken(token: string): Observable<object>;

	abstract checkEmailAvailability(email: string, oidcToken?: string): Observable<EmailAvailability>;

	abstract unsubscribeFromNewsletter(token: string): Observable<object>;

	abstract acceptTermsAndConditions(token: string): Observable<object>;

	abstract acceptLatestTermsAndConditions(token: string): Observable<void>;

	abstract declineTermsAndConditions(token: string): Observable<object>;

	abstract acceptDepositTermsAndConditions(): Observable<Partial<Customer>>;

	abstract acceptPrivacyPolicy(token: string): Observable<void>;

	abstract acceptEntityLegalNotice(token: string): Observable<void>;

	abstract confirmProfile(
		requestData: ConfirmPegaCustomerProfileRequest | ConfirmCogaCustomerProfileRequest,
		countryCode?: string
	): Observable<void>;

	abstract updateProfileValidation(): Observable<void>;
}
